/**
 * Comment Section Component
 * =========================
 * Full comment section with list and form for a video.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { commentService, type CommentWithUser } from '../../services'
import { useAuthStore } from '../../stores'
import { Spinner } from '../common'
import { CommentForm } from './CommentForm'
import { CommentItem } from './CommentItem'

interface CommentSectionProps {
  videoId: string
  videoOwnerId: string
}

export function CommentSection({ videoId, videoOwnerId }: CommentSectionProps) {
  const { user } = useAuthStore()
  const [comments, setComments] = useState<CommentWithUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadComments = async () => {
    try {
      const data = await commentService.getVideoComments(videoId)
      setComments(data)
      setError(null)
    } catch (err) {
      setError('Failed to load comments')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadComments()
  }, [videoId])

  const handleCreateComment = async (content: string) => {
    if (!user) return

    const newComment = await commentService.createComment({
      video_id: videoId,
      user_id: user.id,
      content,
    })

    // Add to top-level comments
    setComments((prev) => [...prev, { ...newComment, replies: [] }])
  }

  const handleReply = async (parentId: string, content: string) => {
    if (!user) return

    const newReply = await commentService.createComment({
      video_id: videoId,
      user_id: user.id,
      parent_id: parentId,
      content,
    })

    // Add reply to the correct parent
    setComments((prev) => addReplyToComments(prev, parentId, newReply))
  }

  const addReplyToComments = (
    comments: CommentWithUser[],
    parentId: string,
    reply: CommentWithUser
  ): CommentWithUser[] => {
    return comments.map((comment) => {
      if (comment.id === parentId) {
        return {
          ...comment,
          replies: [...(comment.replies || []), { ...reply, replies: [] }],
        }
      }
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: addReplyToComments(comment.replies, parentId, reply),
        }
      }
      return comment
    })
  }

  const handleEdit = async (commentId: string, content: string) => {
    const updated = await commentService.updateComment(commentId, { content })
    setComments((prev) => updateCommentInTree(prev, commentId, updated))
  }

  const handleDelete = async (commentId: string) => {
    await commentService.deleteComment(commentId)
    setComments((prev) => removeCommentFromTree(prev, commentId))
  }

  const handleHide = async (commentId: string) => {
    await commentService.hideComment(commentId)
    setComments((prev) => removeCommentFromTree(prev, commentId))
  }

  const updateCommentInTree = (
    comments: CommentWithUser[],
    commentId: string,
    updated: CommentWithUser
  ): CommentWithUser[] => {
    return comments.map((comment) => {
      if (comment.id === commentId) {
        return { ...updated, replies: comment.replies }
      }
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentInTree(comment.replies, commentId, updated),
        }
      }
      return comment
    })
  }

  const removeCommentFromTree = (
    comments: CommentWithUser[],
    commentId: string
  ): CommentWithUser[] => {
    return comments
      .filter((comment) => comment.id !== commentId)
      .map((comment) => ({
        ...comment,
        replies: comment.replies
          ? removeCommentFromTree(comment.replies, commentId)
          : [],
      }))
  }

  const totalComments = countComments(comments)

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">
        Comments {totalComments > 0 && `(${totalComments})`}
      </h2>

      {/* Comment form */}
      {user ? (
        <div className="mb-6">
          <CommentForm
            onSubmit={handleCreateComment}
            placeholder="Add a comment..."
          />
        </div>
      ) : (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-gray-600">
            <Link to="/login" className="text-blue-600 hover:underline">
              Sign in
            </Link>{' '}
            to leave a comment
          </p>
        </div>
      )}

      {/* Comments list */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-600">{error}</div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={user?.id}
              videoOwnerId={videoOwnerId}
              onReply={handleReply}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onHide={user?.id === videoOwnerId ? handleHide : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function countComments(comments: CommentWithUser[]): number {
  return comments.reduce((count, comment) => {
    return count + 1 + (comment.replies ? countComments(comment.replies) : 0)
  }, 0)
}
